import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { PeakMark } from "./PeakMark";
import { Contours, EASE } from "./Reveal";

const LINES = ["We build useful", "products around", "real problems."];

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contoursY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 60, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="grain relative overflow-hidden bg-cream"
    >
      <motion.div style={reduce ? undefined : { y: contoursY }} className="absolute inset-0 text-evergreen/[0.06]">
        <Contours className="h-full w-full" />
      </motion.div>

      <motion.div
        style={reduce ? undefined : { y: markY, rotateX, rotateY, transformPerspective: 900 }}
        className="pointer-events-none absolute -right-16 top-1/2 hidden -translate-y-1/2 opacity-[0.14] lg:block"
        aria-hidden="true"
      >
        <PeakMark className="h-[520px] w-auto" />
      </motion.div>

      <div className="relative mx-auto max-w-[1240px] px-4 pb-24 pt-20 sm:px-6 sm:pt-28 lg:px-8 lg:pb-36 lg:pt-36">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="font-display text-xs font-semibold uppercase tracking-[0.22em] text-growth sm:text-sm"
        >
          Founder-led product company
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="mt-6 font-display text-[44px] font-semibold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[84px]"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {i === 2 ? (
                  <>
                    real <span className="text-growth">problems.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.62, ease: EASE }}
          className="mt-7 max-w-[620px] font-body text-lg leading-relaxed text-slate sm:text-xl"
        >
          Build.Make.Win turns firsthand problems into focused products designed to save time,
          reduce friction, and create lasting value.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.78, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="https://trycoacher.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-explore-coacher-btn"
            className="group inline-flex items-center gap-2 rounded-xl bg-growth px-7 py-3.5 font-display text-base font-semibold text-white transition-colors duration-200 hover:bg-evergreen"
          >
            Explore CoachER
            <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
          <a
            href="#mission"
            data-testid="hero-our-mission-btn"
            className="inline-flex items-center rounded-xl border border-evergreen/40 px-7 py-3.5 font-display text-base font-semibold text-evergreen transition-colors duration-200 hover:bg-evergreen hover:text-white"
          >
            Our mission
          </a>
        </motion.div>
      </div>
    </section>
  );
};
